"use client";

import { Form } from "@/components/ui/form";
import { TSelect } from "@/types/common/select";
import { TJob } from "@/types/jobs";
import { ErrorPageEvent } from "@/utils/events";
import { zodResolver } from "@hookform/resolvers/zod";
import { Loader2Icon } from "lucide-react";
import Link from "next/link";
import { useState } from "react";
import { useForm } from "react-hook-form";
import { z } from "zod";
import CImage from "../CImage";
import CustomDatePicker from "../generator/fields/CustomDatePicker";
import CustomFile from "../generator/fields/CustomFile";
import CustomInput from "../generator/fields/CustomInput";
import CustomSelect from "../generator/fields/CustomSelect";
import CustomTextArea from "../generator/fields/CustomTextArea";
import { Button } from "../ui/button";
type Props = {};

const referenceList: TSelect[] = [
  { id: 1, label: "Karriere.at", value: "Karriere" },
  { id: 2, label: "Willhaben", value: "WILLHABEN" },
  { id: 3, label: "Hokify", value: "HOKIFY" },
  { id: 4, label: "Facebook", value: "FACEBOOK" },
  { id: 5, label: "LinkedIn", value: "LINKEDIN" },
  { id: 6, label: "Anderes", value: "ANDERES" },
];
const MAX_FILE_SIZE = 5000000;
const formSchema = z.object({
  name: z.string().min(2, {
    message: "Required",
  }),
  email: z.string().email("Dies ist keine gültige E-Mail."),
  phone: z.string().min(2, {
    message: "Required",
  }),
  date: z.date(),
  reference: z.string().min(2, {
    message: "Required",
  }),
  message: z.string().min(2, {
    message: "Required",
  }),
  file: z.any(),
});

type Inputs = z.infer<typeof formSchema>;

const FormJobs = (props: TJob) => {
  const form = useForm<Inputs>({
    resolver: zodResolver(formSchema),
    mode: "onChange",
  });

  const [isSending, setIsSending] = useState(false);
  const [isError, setIsError] = useState(false);
  const [isSuccess, setIsSuccess] = useState(false);

  const {
    register,
    handleSubmit,
    watch,
    setValue,
    reset,
    trigger,
    clearErrors,
    formState: { errors },
  } = form;
  function onSubmit(values: z.infer<typeof formSchema>) {
    sendValuesToApi(values);
  }
  const sendValuesToApi = async (values: z.infer<typeof formSchema>) => {
    setIsSending(true);
    const formData = new FormData();
    formData.append("partner_name", values.name);
    formData.append("email_from", values.email);
    formData.append("partner_phone", values.phone);
    formData.append("date", values.date.toString());
    formData.append("reference", values.reference);
    formData.append("message", values.message);
    formData.append("attachment", values.file);
    formData.append("job_id", props.jobId);
    // formData.append("job_id", "9");
    try {
      const response = await fetch(
        `${process.env.NEXT_PUBLIC_DOMAIN_ROUTE}/api/odoo`,
        {
          method: "POST",
          body: formData,
        }
      );
      let resp = await response.json();
      if (resp.id) {
        setIsSuccess(true);
      } else {
        console.log("Error", resp);
        await ErrorPageEvent(JSON.stringify(resp), JSON.stringify(values));
        setIsError(true);
      }
    } catch (e) {
      await ErrorPageEvent("500", JSON.stringify(values));
      setIsError(true);
    }
  };
  return (
    <section className="w-full bg-secondary-200 py-16" id="formJob">
      <div className="container mx-auto grid grid-cols-1 gap-4 lg:grid-cols-3 lg:gap-8">
        <div className="flex h-full w-full flex-grow flex-col justify-between gap-8 p-4 lg:pb-20">
          <span className="flex flex-col gap-2">
            <h3>Bewerbungsformular</h3>
            <p className="text-xl">{props.positionName}</p>
          </span>
          <div className="hidden w-full flex-col p-4 lg:flex">
            <CImage
              {...props.place.storeImage}
              src={props.place.storeImage.url}
              width={600}
              height={500}
              crop
              className="h-auto w-full"
            />
            <div className="flex flex-col bg-secondary p-4 text-white">
              <h5>{props.place.storeName}</h5>
              <p>{props.place.storeDistrict}</p>
              <p>
                {props.place.storeAddress}, {props.place.storeCity}
              </p>
            </div>
          </div>
        </div>
        {!isError && !isSuccess && (
          <div className="flex flex-grow flex-col gap-4 p-4 lg:col-span-2">
            <Form {...form}>
              <form
                onSubmit={form.handleSubmit(onSubmit)}
                className="flex flex-grow flex-col gap-4"
              >
                <div className=" flex flex-col gap-3 rounded-xl lg:flex-row">
                  <div className="flex w-full flex-grow flex-col items-start justify-start gap-3 rounded-lg ">
                    <CustomInput
                      form={form}
                      name="name"
                      label="Dein Name *"
                      placeholder=""
                    />
                    <CustomInput
                      form={form}
                      name="email"
                      label="Deine E-Mail *"
                      placeholder=""
                    />
                    <CustomInput
                      form={form}
                      name="phone"
                      type="tel"
                      label="Deine Telefonnummer *"
                      placeholder=""
                      inputmode="numeric"
                      pattern="[0-9\s]{13,19}"
                    />
                    <CustomDatePicker
                      form={form}
                      name="date"
                      label="Was wäre dein frühester Arbeitsbeginn?"
                      placeholder="Wählen Sie das Datum aus"
                    />
                    <CustomSelect
                      form={form}
                      name="reference"
                      label="Wie bist du auf diese Stelle aufmerksam geworden?"
                      placeholder="Wählen Sie die Referenz aus"
                      list={referenceList}
                    />
                    <CustomTextArea
                      form={form}
                      name="message"
                      label="Erzähle uns kurz, weshalb du dich für die Stelle interessierst. *"
                      placeholder=""
                    />
                    <CustomFile
                      form={form}
                      name="file"
                      type="file"
                      label="Dein Lebenslauf*"
                      placeholder=""
                    />
                  </div>
                </div>

                <Button
                  type="submit"
                  className="button large secondary solid lg:w-3xl group mx-auto mb-4 w-full !py-6 text-center uppercase tracking-wider"
                  disabled={isSending}
                >
                  {isSending && (
                    <Loader2Icon className="h-r w-4 animate-spin text-white" />
                  )}
                  Jetzt Bewerbung Absenden!
                </Button>
              </form>
            </Form>
          </div>
        )}
        {isError && (
          <div className="flex flex-grow flex-col items-center justify-center gap-10  text-lg lg:col-span-2 lg:pb-20">
            <span className="flex flex-col items-center justify-center gap-2">
              <h3 className="text-secondary">Oops! </h3>
              <p>Etwas ist schief gelaufen!</p>
              <p>Bitte versuchen Sie es später noch einmal!</p>
            </span>
            <span className="flex flex-col items-center justify-center gap-2">
              <p>
                Oder wenn Sie es vorziehen, kontaktieren Sie uns telefonisch:
              </p>
              <a
                href="tel:+43800884404"
                className="button large secondary solid w-xl group mx-auto mb-4 mt-2 !py-4 text-center uppercase tracking-wider"
              >
                0800 88 44 04
              </a>
            </span>
          </div>
        )}
        {isSuccess && (
          <div className="flex flex-grow flex-col items-center justify-center gap-10  text-lg lg:col-span-2 lg:pb-20">
            <span className="flex flex-col items-center justify-center gap-2">
              <h3 className="text-secondary">Gratulation! </h3>
              <p>Ihre Bewerbung wurde erfolgreich gesendet.</p>
            </span>
            <span className="flex flex-col items-center justify-center gap-2">
              <p>In der Zwischenzeit</p>
              <p>Schauen Sie sich auf unserer Website um:</p>
              <Link
                href="/"
                className="button large secondary solid w-3xl group mx-auto mb-4 mt-2 !py-4 text-center uppercase tracking-wider"
              >
                Fortfahren auf unserer Website
              </Link>
            </span>
          </div>
        )}
      </div>
    </section>
  );
};

export default FormJobs;
